<template>
  <div
    ref="containerRef"
    class="zoom-layer"
    @wheel.prevent="onWheel"
    @mousedown="onMouseDown"
    @dragstart.prevent
    @dblclick="resetView"
    @touchstart.passive="onTouchStart"
    @touchmove.prevent="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <div
      ref="contentRef"
      class="zoom-layer__content"
      :class="{
        'zoom-layer__content--dragging': isMouseDragging,
      }"
      :style="contentStyle"
      @dragstart.prevent
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

type Point = { x: number; y: number };

const props = withDefaults(
  defineProps<{
    minScale?: number;
    maxScale?: number;
    wheelStep?: number;
    reloadKey?: string | number;
  }>(),
  {
    minScale: 0.5,
    maxScale: 5,
    wheelStep: 0.12,
    reloadKey: "noKey",
  },
);

const containerRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);

const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);

const isMouseDragging = ref(false);
const mouseDragStart = ref<Point>({ x: 0, y: 0 });
const mouseDragOrigin = ref<Point>({ x: 0, y: 0 });

const contentStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  transformOrigin: "0 0",
  cursor: scale.value <= 1 ? "default" : isMouseDragging.value ? "grabbing" : "grab",
}));

let resizeObserver: ResizeObserver | null = null;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function getContainerRect() {
  return containerRef.value?.getBoundingClientRect();
}

function toLocalPoint(clientX: number, clientY: number): Point {
  const rect = getContainerRect();
  if (!rect) return { x: clientX, y: clientY };
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
}

function zoomAt(clientX: number, clientY: number, newScale: number) {
  const clampedScale = clamp(newScale, props.minScale, props.maxScale);
  const scaleRatio = clampedScale / scale.value;
  const localPoint = toLocalPoint(clientX, clientY);

  translateX.value = localPoint.x - (localPoint.x - translateX.value) * scaleRatio;
  translateY.value = localPoint.y - (localPoint.y - translateY.value) * scaleRatio;

  scale.value = clampedScale;
  constrainTranslation();
}

function constrainTranslation() {
  const container = containerRef.value;
  const content = contentRef.value;
  if (!container || !content) return;

  const containerRect = container.getBoundingClientRect();
  const unscaledWidth = content.offsetWidth;
  const unscaledHeight = content.offsetHeight;

  if (!unscaledWidth || !unscaledHeight) return;

  const scaledWidth = unscaledWidth * scale.value;
  const scaledHeight = unscaledHeight * scale.value;

  if (scaledWidth <= containerRect.width) {
    translateX.value = (containerRect.width - scaledWidth) / 2;
  } else {
    translateX.value = clamp(translateX.value, containerRect.width - scaledWidth, 0);
  }

  if (scaledHeight <= containerRect.height) {
    translateY.value = (containerRect.height - scaledHeight) / 2;
  } else {
    translateY.value = clamp(translateY.value, containerRect.height - scaledHeight, 0);
  }
}

function recenter() {
  constrainTranslation();
}

async function resetView() {
  scale.value = 1;
  translateX.value = 0;
  translateY.value = 0;
  await nextTick();
  constrainTranslation();
}

watch(
  () => props.reloadKey,
  async () => {
    await resetView();
  },
);

function onWheel(event: WheelEvent) {
  const direction = event.deltaY > 0 ? -1 : 1;
  const factor = 1 + props.wheelStep * direction;
  zoomAt(event.clientX, event.clientY, scale.value * factor);
}

function onMouseDown(event: MouseEvent) {
  if (event.button !== 0) {
    return;
  }

  event.preventDefault();

  isMouseDragging.value = true;
  mouseDragStart.value = { x: event.clientX, y: event.clientY };
  mouseDragOrigin.value = {
    x: translateX.value,
    y: translateY.value,
  };

  document.body.classList.add("zoom-layer--dragging");

  window.addEventListener("mousemove", onMouseMove, { passive: false });
  window.addEventListener("mouseup", onMouseUp);
}

function onMouseMove(event: MouseEvent) {
  if (!isMouseDragging.value) return;

  event.preventDefault();

  const dx = event.clientX - mouseDragStart.value.x;
  const dy = event.clientY - mouseDragStart.value.y;

  translateX.value = mouseDragOrigin.value.x + dx;
  translateY.value = mouseDragOrigin.value.y + dy;
  constrainTranslation();
}

function onMouseUp() {
  isMouseDragging.value = false;
  document.body.classList.remove("zoom-layer--dragging");
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
}

function onTouchStart() {}
function onTouchMove() {}
function onTouchEnd() {}

onMounted(async () => {
  await nextTick();
  recenter();

  resizeObserver = new ResizeObserver(() => {
    recenter();
  });

  if (containerRef.value) {
    resizeObserver.observe(containerRef.value);
  }

  if (contentRef.value) {
    resizeObserver.observe(contentRef.value);
  }
});

onBeforeUnmount(() => {
  document.body.classList.remove("zoom-layer--dragging");
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
  resizeObserver?.disconnect();
});
</script>

<style scoped>
.zoom-layer {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.zoom-layer__content {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  will-change: transform;
}

.zoom-layer__content--dragging {
  pointer-events: none;
}

.zoom-layer :deep(img) {
  -webkit-user-drag: none;
  user-drag: none;
  pointer-events: none;
}

.zoom-layer :deep(a) {
  -webkit-user-drag: none;
  user-drag: none;
}

:global(body.zoom-layer--dragging) {
  user-select: none;
  -webkit-user-select: none;
  cursor: grabbing;
}
</style>
